import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    DateField,
    SearchInput,
    TopToolbar,
    FilterButton,
    CreateButton,
    Pagination,
    DatagridLoading,
    WithListContext,
    useListContext,
    useDataProvider,
    useRefresh,
    useNotify,
    useUnselectAll,
    useTranslate,
} from 'react-admin';
import {
    Chip,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';

const filters = [<SearchInput source="q" alwaysOn />];

const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
    </TopToolbar>
);

const BulkDeleteButton = () => {
    const { selectedIds, data } = useListContext();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const notify = useNotify();
    const unselectAll = useUnselectAll('admin/oauth-clients');
    const translate = useTranslate();
    const [open, setOpen] = React.useState(false);

    const selectedRecords = (data || []).filter((r: any) =>
        selectedIds.includes(r.id)
    );
    const permanentIds = selectedRecords
        .filter((r: any) => r.status === 1)
        .map((r: any) => r.id);
    const softIds = selectedRecords
        .filter((r: any) => r.status !== 1)
        .map((r: any) => r.id);
    const hasPermanent = permanentIds.length > 0;

    const handleClick = () => {
        if (hasPermanent) {
            setOpen(true);
        } else {
            executeSoftDelete();
        }
    };

    const executeSoftDelete = async () => {
        try {
            await Promise.all(
                softIds.map((id: string) =>
                    dataProvider.delete('admin/oauth-clients', {
                        id,
                        previousData: { id },
                    })
                )
            );
            notify(
                translate(
                    'hallinta.oauth_clients.bulk_delete.success_disabled',
                    { count: softIds.length }
                ),
                { type: 'success' }
            );
            unselectAll();
            refresh();
        } catch (e: any) {
            notify(translate('hallinta.oauth_clients.bulk_delete.error'), {
                type: 'error',
            });
        }
    };

    const executeDelete = async () => {
        setOpen(false);
        try {
            if (softIds.length > 0) {
                await Promise.all(
                    softIds.map((id: string) =>
                        dataProvider.delete('admin/oauth-clients', {
                            id,
                            previousData: { id },
                        })
                    )
                );
            }
            if (permanentIds.length > 0) {
                await Promise.all(
                    permanentIds.map((id: string) =>
                        (dataProvider as any).deleteOAuthClientPermanent(id)
                    )
                );
            }
            const msgs: string[] = [];
            if (softIds.length > 0)
                msgs.push(
                    translate(
                        'hallinta.oauth_clients.bulk_delete.success_disabled',
                        { count: softIds.length }
                    )
                );
            if (permanentIds.length > 0)
                msgs.push(
                    translate(
                        'hallinta.oauth_clients.bulk_delete.success_permanent',
                        { count: permanentIds.length }
                    )
                );
            notify(msgs.join(', '), { type: 'success' });
            unselectAll();
            refresh();
        } catch (e: any) {
            notify(translate('hallinta.oauth_clients.bulk_delete.error'), {
                type: 'error',
            });
        }
    };

    return (
        <>
            <Button
                startIcon={<DeleteIcon />}
                onClick={handleClick}
                color="error"
                size="small"
            >
                {translate('hallinta.oauth_clients.bulk_delete.button', {
                    count: selectedIds.length,
                })}
            </Button>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>
                    {translate(
                        'hallinta.oauth_clients.bulk_delete.confirm_title'
                    )}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {translate(
                            'hallinta.oauth_clients.bulk_delete.confirm_permanent',
                            { count: permanentIds.length }
                        )}
                        {softIds.length > 0 && (
                            <>
                                <br />
                                <br />
                                {translate(
                                    'hallinta.oauth_clients.bulk_delete.confirm_soft',
                                    { count: softIds.length }
                                )}
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        {translate('hallinta.oauth_clients.bulk_delete.cancel')}
                    </Button>
                    <Button
                        onClick={executeDelete}
                        color="error"
                        variant="contained"
                    >
                        {translate(
                            'hallinta.oauth_clients.bulk_delete.confirm'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const OAuthClientList = ({ resource }: { resource?: string }) => {
    const translate = useTranslate();

    return (
        <List
            resource={resource || 'admin/oauth-clients'}
            {...(resource
                ? {
                      storeKey: 'oauthClientListEmbedded',
                      disableSyncWithLocation: true,
                  }
                : {})}
            filters={filters}
            sort={{ field: 'name', order: 'ASC' }}
            perPage={50}
            pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
            actions={<ListActions />}
        >
            <WithListContext
                render={({ isLoading, isFetching }) =>
                    isLoading || isFetching ? (
                        <DatagridLoading nbChildren={5} nbFakeLines={8} />
                    ) : (
                        <Datagrid
                            rowClick="edit"
                            bulkActionButtons={<BulkDeleteButton />}
                        >
                            <TextField
                                source="name"
                                label={translate(
                                    'hallinta.oauth_clients.fields.name'
                                )}
                            />
                            <FunctionField
                                source="type"
                                label={translate(
                                    'hallinta.oauth_clients.fields.type'
                                )}
                                render={record => {
                                    const type = record?.type;
                                    if (!type) return '—';
                                    const colorMap: Record<
                                        string,
                                        'primary' | 'secondary' | 'warning'
                                    > = {
                                        openid: 'primary',
                                        apikey: 'secondary',
                                        basicauth: 'warning',
                                    };
                                    return (
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <Chip
                                                label={type}
                                                size="small"
                                                color={
                                                    colorMap[type] || 'default'
                                                }
                                                variant="outlined"
                                            />
                                            {record?.allow_basic_auth && (
                                                <Chip
                                                    icon={
                                                        <VpnKeyOutlinedIcon />
                                                    }
                                                    label={translate(
                                                        'hallinta.oauth_clients.fields.basic_auth_badge'
                                                    )}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Stack>
                                    );
                                }}
                            />
                            <TextField
                                source="client_id"
                                label={translate(
                                    'hallinta.oauth_clients.fields.client_id'
                                )}
                            />
                            <FunctionField
                                source="env"
                                label={translate(
                                    'hallinta.oauth_clients.fields.env'
                                )}
                                render={record => {
                                    const env = record?.env;
                                    if (
                                        !env ||
                                        (Array.isArray(env) && env.length === 0)
                                    )
                                        return '—';
                                    const envArr = Array.isArray(env)
                                        ? env
                                        : [env];
                                    return (
                                        <Stack direction="row" spacing={0.5}>
                                            {envArr.map((e: string) => (
                                                <Chip
                                                    key={e}
                                                    label={e}
                                                    size="small"
                                                    color="info"
                                                />
                                            ))}
                                        </Stack>
                                    );
                                }}
                            />
                            <FunctionField
                                source="status"
                                label={translate(
                                    'hallinta.oauth_clients.fields.status'
                                )}
                                render={record => {
                                    const isActive = record?.status === 0;
                                    return (
                                        <Chip
                                            label={translate(
                                                `hallinta.oauth_clients.status.${isActive ? 'active' : 'disabled'}`
                                            )}
                                            color={
                                                isActive ? 'success' : 'error'
                                            }
                                            size="small"
                                        />
                                    );
                                }}
                            />
                            <FunctionField
                                source="applications"
                                label={translate(
                                    'hallinta.oauth_clients.fields.applications'
                                )}
                                sortable={false}
                                render={record => {
                                    const apps = record?.applications || [];
                                    if (
                                        !Array.isArray(apps) ||
                                        apps.length === 0
                                    )
                                        return '—';
                                    const isLocalhostUri = (uri: any) =>
                                        typeof uri === 'string' &&
                                        /localhost|127\.0\.0\.1/i.test(uri);
                                    const sortedApps = [...apps].sort(
                                        (a: any, b: any) =>
                                            Number(
                                                isLocalhostUri(a.login_uri)
                                            ) -
                                            Number(isLocalhostUri(b.login_uri))
                                    );
                                    return (
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            flexWrap="wrap"
                                        >
                                            {sortedApps.map((app: any) => {
                                                const isLocalhost =
                                                    isLocalhostUri(
                                                        app.login_uri
                                                    );
                                                return (
                                                    <Chip
                                                        key={app.guid}
                                                        label={
                                                            app.application_name
                                                        }
                                                        size="small"
                                                        color={
                                                            isLocalhost
                                                                ? 'warning'
                                                                : 'default'
                                                        }
                                                        variant="outlined"
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    );
                                }}
                            />
                            <DateField
                                source="created"
                                label={translate(
                                    'hallinta.oauth_clients.fields.created'
                                )}
                                showTime={false}
                                locales="fi-FI"
                            />
                        </Datagrid>
                    )
                }
            />
        </List>
    );
};

export default OAuthClientList;
